/**
 * Voice Commands Help Overlay
 * 
 * Shows available voice commands and how to use speech-to-text
 * in the command palette for hands-free navigation.
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mic, MicOff, Keyboard, Command, Search, Volume2, Info } from "lucide-react";

const VOICE_COMMANDS = [
  {
    category: "Documentation",
    commands: [
      { phrase: "new visit note", description: "Create a new visit note" },
      { phrase: "smart documentation", description: "Open AI-powered documentation" },
      { phrase: "medication record", description: "Open medication administration record" },
    ]
  },
  {
    category: "Clinical",
    commands: [
      { phrase: "vital signs", description: "Record patient vital signs" },
      { phrase: "pain management", description: "Open pain assessment" },
      { phrase: "wound care", description: "Document wound care assessment" },
      { phrase: "symptom assessment", description: "Open symptom tracker" },
      { phrase: "fall event", description: "Document a fall incident" },
    ]
  },
  {
    category: "IDG & Meetings",
    commands: [
      { phrase: "IDG voice meeting", description: "Start IDG meeting with transcription" },
      { phrase: "IDG analytics", description: "View meeting analytics dashboard" },
      { phrase: "IDG email settings", description: "Configure meeting report emails" },
    ]
  },
  {
    category: "Navigation",
    commands: [
      { phrase: "care calendar", description: "View patient care calendar" },
      { phrase: "schedule visit", description: "Schedule a new visit" },
      { phrase: "plan of care", description: "View/edit plan of care" },
      { phrase: "compliance dashboard", description: "Open compliance dashboard" },
    ]
  },
  {
    category: "Communication",
    commands: [
      { phrase: "family message", description: "Send message to family" },
      { phrase: "log phone call", description: "Document a phone call" },
      { phrase: "IDG discussion", description: "Add IDG discussion note" },
    ]
  }
];

export default function VoiceHelpOverlay({ isOpen, onClose }) {
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-[600px] sm:max-w-[600px] bg-slate-900 border-slate-700 overflow-y-auto"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="text-white flex items-center gap-2">
            <Mic className="w-5 h-5 text-teal-400" />
            Voice Commands Help
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Use your voice for hands-free navigation in HOS
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6" data-testid="voice-help-overlay">
          {/* How to Use */}
          <Card className="bg-teal-900/20 border-teal-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Info className="w-4 h-4 text-teal-400" />
                How to Use Voice Search
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-xs shrink-0">1</div>
                <div>
                  <p className="text-white font-medium">Open Command Palette</p>
                  <p className="text-slate-400">Press <Badge variant="outline" className="mx-1">⌘K</Badge> or <Badge variant="outline" className="mx-1">Ctrl+K</Badge></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-xs shrink-0">2</div>
                <div>
                  <p className="text-white font-medium">Click the Microphone</p>
                  <p className="text-slate-400">Look for <Mic className="inline w-4 h-4 text-teal-400" /> button next to search</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-xs shrink-0">3</div>
                <div>
                  <p className="text-white font-medium">Speak Your Command</p>
                  <p className="text-slate-400">Say what you want to do, e.g., &quot;new visit note&quot;</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-xs shrink-0">4</div>
                <div>
                  <p className="text-white font-medium">Press Enter to Execute</p>
                  <p className="text-slate-400">Select the matching command and press Enter</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice Commands List */}
          <div>
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-purple-400" />
              Available Voice Commands
            </h3>
            <ScrollArea className="h-[400px]">
              <div className="space-y-4 pr-4">
                {VOICE_COMMANDS.map(category => (
                  <Card key={category.category} className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="py-3">
                      <CardTitle className="text-white text-sm">{category.category}</CardTitle>
                    </CardHeader>
                    <CardContent className="py-0 pb-3">
                      <div className="space-y-2">
                        {category.commands.map(cmd => (
                          <div key={cmd.phrase} className="flex items-center justify-between py-1.5 border-b border-slate-700/50 last:border-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-slate-700 text-teal-300 font-mono text-xs">
                                &quot;{cmd.phrase}&quot;
                              </Badge>
                            </div>
                            <span className="text-slate-400 text-xs">{cmd.description}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Tips */}
          <Card className="bg-amber-900/20 border-amber-500/30">
            <CardContent className="py-3">
              <p className="text-amber-300 text-sm">
                <strong>💡 Tip:</strong> Speak naturally! The voice search understands partial phrases and keywords. 
                For example, saying &quot;IDG&quot; will show all IDG-related commands.
              </p>
            </CardContent>
          </Card>

          {/* Keyboard Shortcuts */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="py-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Keyboard className="w-4 h-4 text-blue-400" />
                Keyboard Shortcuts
              </CardTitle>
            </CardHeader>
            <CardContent className="py-0 pb-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Open Palette</span>
                  <Badge variant="outline" className="text-slate-300">⌘K / Ctrl+K</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Close</span>
                  <Badge variant="outline" className="text-slate-300">Esc</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Navigate</span>
                  <Badge variant="outline" className="text-slate-300">↑↓</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Execute</span>
                  <Badge variant="outline" className="text-slate-300">Enter</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
